import { Injectable, Type } from '@angular/core';
import {
  ApiPaginatedResponse,
  ApiQuery,
  BaseApiService,
  apiQueryToHttpParams,
} from '@kinet-open-network/client/core/api-client';
import {
  AutomationActionType,
  AutomationContext,
  AutomationProducerContextDataMap,
  IAutomationData,
  IAutomationExecution,
  IAutomationExecutionDetail,
} from '@kinet-open-network/shared/data-access/model';
import { Automation } from '../models/automation.model';

@Injectable({
  providedIn: 'root',
})
export class AutomationsService extends BaseApiService<Automation> {
  override model: Type<Automation> = Automation;

  override get endpoint(): string {
    return '/automations';
  }

  execute(automationId: string) {
    return this.http.post<void>(`${this.endpoint}/${automationId}/execute`, {});
  }

  runAction<T extends AutomationContext>(
    actionType: AutomationActionType,
    context: T,
    contextData: AutomationProducerContextDataMap[T],
    data?: Record<string, unknown>,
  ) {
    return this.http.post<void>(`${this.endpoint}/run-action`, {
      actionType,
      context,
      contextData,
      data,
    });
  }

  /** Runs the automation without side effects; `definition` overrides the saved one (unsaved edits). */
  dryRun(
    automationId: string,
    contextData: Record<string, unknown>,
    definition?: Partial<IAutomationData>,
  ) {
    return this.http.post<IAutomationExecutionDetail>(
      `${this.endpoint}/${automationId}/dry-run`,
      { contextData, definition },
    );
  }

  /** Paginated executions of the automation; supports the common resource query (page, sort, filters). */
  queryExecutions(automationId: string, query?: ApiQuery | null) {
    return this.http.get<ApiPaginatedResponse<IAutomationExecution>>(
      `${this.endpoint}/${automationId}/executions`,
      { params: apiQueryToHttpParams(query) },
    );
  }

  getExecution(executionId: string) {
    return this.http.get<IAutomationExecutionDetail>(
      `${this.endpoint}/executions/${executionId}`,
    );
  }
}
